'use client'

import { useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNowStrict } from 'date-fns'
import { AnimatePresence } from 'framer-motion'
import { type ExtractAtomArgs, type ExtractAtomResult, useAtomValue, useSetAtom } from 'jotai'
import { selectAtom } from 'jotai/utils'
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  InfoIcon,
  Loader2Icon,
  LogOutIcon,
  PencilIcon,
  PercentIcon,
  PlusIcon,
  RefreshCwIcon,
  TagIcon,
  Trash2Icon,
  Unplug,
  UsersIcon,
  XCircleIcon,
  XIcon,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { cloneElement, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ApiCredentialsForm } from '@/components/dialog/settings/api-credentials-form'
import { DestructiveConfirmDialog } from '@/components/dialog/settings/destructive-confirm-dialog'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { ManageAccessPanel } from '@/features/account-access/components/manage-access-panel'
import { PendingInvites } from '@/features/account-access/components/pending-invites'
import { RenameAccountDialog } from '@/features/account-access/components/rename-account-dialog'
import {
  accountRoleTranslationKey,
  countAccountsAgainstPlanCap,
  isAccountOwner,
} from '@/features/account-access/lib/roles'
import { AccountConnectionSheet } from '@/features/dashboard/components/account-connection/account-connection-sheet'
import { LighterFeeConsentSheet } from '@/features/dashboard/components/account-connection/lighter-fee-consent-sheet'
import { LighterKeyRelinkSheet } from '@/features/dashboard/components/account-connection/lighter-key-relink-sheet'
import { PapertradeSignStep } from '@/features/dashboard/components/account-connection/papertrade-sign-step'
import { ParadigmBindingRow } from '@/features/dashboard/components/account-connection/paradigm-binding-row'
import { TourSpotlight, type TourTarget } from '@/features/onboarding/components/spotlight'
import { useCapabilities } from '@/hooks/use-capabilities'
import { useDeriveOnboard } from '@/hooks/use-derive-onboard'
import { useHyperliquidOnboard } from '@/hooks/use-hyperliquid-onboard'
import { useVenueOauthConnect } from '@/hooks/use-venue-oauth-connect'
import { BILLING_ROUTE } from '@/lib/billing/routes'
import { getExchangeByTradeVenue, SUPPORTING_EXCHANGES } from '@/lib/configs/exchange-registry'
import { connectWallet, signDeriveLogin } from '@/lib/derive/sign-session-key'
import { linkedSubaccountIds } from '@/lib/derive/subaccount-selection'
import { beginManualWalletAction } from '@/lib/lighter/sign-subaccount-key'
import { daysUntil } from '@/lib/papertrade/expiry'
import { supportsParadigmBinding } from '@/lib/paradigm/binding'
import { tradeVenueToIconKey } from '@/lib/proto/util'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import {
  disconnectRoute,
  reauthorizeRoute,
  reconnectBannerKey,
} from '@/lib/utils/agent-wallet-route'
import {
  hyperliquidReconnectState,
  isAgentWalletAccount,
  toAccountStatus,
} from '@/lib/utils/exchange-util'
import { preloadWallet } from '@/lib/wallet/connect'
import { useLeaveAccount } from '@/service/account-access.hook'
import { useCancelPendingAccount, useDisableAccount, useUserAccounts } from '@/service/account.hook'
import {
  useDeriveDisconnect,
  useHyperliquidDisconnect,
  usePapertradeDisconnect,
  useVenues,
} from '@/service/venue.hook'
import { apiCredentialsFormStateAtom } from '@/state/api-credentials'
import { exchangeAtom } from '@/state/storage'
import { AccountAuthMethod, AccountStatus, PublicAccount } from '@/types/generated/common/account'
import { TradeVenue } from '@/types/generated/common/types'
import type { OauthConfig } from '@/types/generated/public_api'

enum ButtonAction {
  DEFAULT = 'DEFAULT',
  EDIT = 'EDIT',
  RENAME = 'RENAME',
  MANAGE = 'MANAGE',
  DELETE = 'DELETE',
  LEAVE = 'LEAVE',
  DISCONNECT = 'DISCONNECT',
  CANCEL_SETUP = 'CANCEL_SETUP',
}

interface SettingAccountProps {
  defaultAccountId?: string
  /** Spotlight the "Add account" button for a few seconds, e.g. arriving from a
   * "connect an account to trade this" toast. */
  highlightAddAccount?: boolean
  highlightAccountId?: string
  /** Open this account's Manage access panel on arrival (e.g. from the Cmd+K palette). */
  manageAccessAccountId?: string
}

const ADD_ACCOUNT_SPOTLIGHT_MS = 4000
const ADD_ACCOUNT_SPOTLIGHT_CLASS =
  'ring-ox-cyan ring-offset-background animate-pulse ring-2 ring-offset-2'

const PENDING_AUTH_REFETCH_INTERVAL_MS = 3000

type AtomSetter<AtomType> = (...args: ExtractAtomArgs<AtomType>) => ExtractAtomResult<AtomType>

const editingAccountIdAtom = selectAtom(
  apiCredentialsFormStateAtom,
  (state) => state.editingAccount?.accountId ?? null
)
const savedExchangeAccountIdAtom = selectAtom(exchangeAtom, (account) => account?.accountId ?? null)

export default function SettingAccount({
  defaultAccountId,
  highlightAddAccount = false,
  highlightAccountId,
  manageAccessAccountId,
}: SettingAccountProps) {
  const t = useTranslations('settings')
  const router = useRouter()
  const queryClient = useQueryClient()
  const editingAccountId = useAtomValue(editingAccountIdAtom)
  const savedExchangeAccountId = useAtomValue(savedExchangeAccountIdAtom)
  const setApiFormState = useSetAtom(apiCredentialsFormStateAtom)
  const setSavedExchange = useSetAtom(exchangeAtom)
  const accountListRef = useRef<HTMLDivElement>(null)
  const [accountSpotlight, setAccountSpotlight] = useState<TourTarget | null>(null)
  const [showDisabled, setShowDisabled] = useState(Boolean(highlightAccountId))
  const [showAddAccount, setShowAddAccount] = useState(false)
  const [addAccountSpotlit, setAddAccountSpotlit] = useState(highlightAddAccount)
  useEffect(() => {
    if (!addAccountSpotlit) return
    const timeoutId = window.setTimeout(() => setAddAccountSpotlit(false), ADD_ACCOUNT_SPOTLIGHT_MS)
    return () => window.clearTimeout(timeoutId)
  }, [addAccountSpotlit])
  useEffect(() => preloadWallet(), [])
  const { data: userAccounts, isLoading: isUserAccountsLoading } = useUserAccounts({
    showDisabled: true,
    // The shared account query stays fresh for five minutes. Settings should
    // still check the server whenever this screen is opened so a recently
    // authorized account does not keep showing a cached Pending Auth state.
    refetchOnMount: 'always',
  })
  const isAccountsLoading = isUserAccountsLoading && !userAccounts
  const { data: venueList } = useVenues()
  const { maxAccounts } = useCapabilities()
  // `showDisabled: true` above is for the suspended-accounts toggle, so this
  // list includes deleted accounts — they must not be counted (see the helper).
  const atAccountCap = countAccountsAgainstPlanCap(userAccounts?.accounts ?? []) >= maxAccounts
  const addableExchanges = useMemo(() => {
    if (!venueList?.venues.length) return SUPPORTING_EXCHANGES
    const venuesOpenForSignup = new Set(
      venueList.venues.filter((venue) => venue.newAccount).map((venue) => venue.tradeVenue)
    )
    return SUPPORTING_EXCHANGES.filter((exchange) => venuesOpenForSignup.has(exchange.id))
  }, [venueList?.venues])
  const oauthConfigByVenue = useMemo(
    () =>
      new Map(
        venueList?.venues
          .filter((venue) => venue.oauth !== undefined)
          .map((venue) => [venue.tradeVenue, venue.oauth] as const) ?? []
      ),
    [venueList?.venues]
  )
  const hasPendingAuthAccount = userAccounts?.accounts.some(
    (account) => account.status === AccountStatus.ACCOUNT_STATUS_PENDING_AUTH
  )

  useEffect(() => {
    if (!hasPendingAuthAccount) return

    const intervalId = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
    }, PENDING_AUTH_REFETCH_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [hasPendingAuthAccount, queryClient])

  const handleAddAccountClick = () => {
    setAddAccountSpotlit(false)
    if (atAccountCap) {
      router.push(BILLING_ROUTE)
      return
    }
    setShowAddAccount(true)
  }
  const accounts = useMemo(() => {
    const sortedAccounts = [...(userAccounts?.accounts ?? [])].sort(
      (a, b) => Number(b.updatedAt) - Number(a.updatedAt)
    )

    if (showDisabled) {
      return sortedAccounts
    }

    return sortedAccounts.filter(
      (account) =>
        ![AccountStatus.ACCOUNT_STATUS_SUSPENDED, AccountStatus.ACCOUNT_STATUS_INACTIVE].includes(
          account.status
        )
    )
  }, [showDisabled, userAccounts?.accounts])

  // Auto-open form for defaultAccountId (credential editing is owner-only)
  useEffect(() => {
    if (defaultAccountId && accounts && accounts.length > 0) {
      const matchedAccount = accounts.find(
        (acc) => acc.accountId === defaultAccountId && isAccountOwner(acc.role)
      )
      if (
        matchedAccount &&
        matchedAccount.authMethod !== AccountAuthMethod.ACCOUNT_AUTH_METHOD_APP_USER &&
        !isAgentWalletAccount(matchedAccount) &&
        editingAccountId !== defaultAccountId
      ) {
        setApiFormState({
          apiKey: '',
          apiSecret: '',
          accountName: matchedAccount.name,
          tradeVenue: matchedAccount.tradeVenue,
          editingAccount: matchedAccount,
        })
      }
    }
  }, [defaultAccountId, accounts, editingAccountId, setApiFormState])

  useEffect(() => {
    if (!highlightAccountId || isAccountsLoading) return
    const target = Array.from(
      accountListRef.current?.querySelectorAll<HTMLElement>('[data-settings-account-id]') ?? []
    ).find((element) => element.dataset.settingsAccountId === highlightAccountId)
    if (!target) return
    let frameId = 0
    let dismissId: ReturnType<typeof setTimeout> | undefined
    const measure = () => {
      const rect = target.getBoundingClientRect()
      setAccountSpotlight((current) =>
        current?.element === target &&
        current.rect.left === rect.left &&
        current.rect.top === rect.top &&
        current.rect.width === rect.width &&
        current.rect.height === rect.height
          ? current
          : { element: target, rect }
      )
      frameId = requestAnimationFrame(measure)
    }
    const dismiss = () => {
      clearTimeout(dismissId)
      cancelAnimationFrame(frameId)
      setAccountSpotlight(null)
    }
    const onOutsidePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !target.contains(event.target)) dismiss()
    }
    const startId = setTimeout(() => {
      target.scrollIntoView({ block: 'center', inline: 'nearest' })
      measure()
      document.addEventListener('pointerdown', onOutsidePointerDown, true)
      dismissId = setTimeout(dismiss, ADD_ACCOUNT_SPOTLIGHT_MS)
    }, 200)
    return () => {
      clearTimeout(startId)
      document.removeEventListener('pointerdown', onOutsidePointerDown, true)
      dismiss()
    }
  }, [highlightAccountId, isAccountsLoading, accounts])

  return (
    <div ref={accountListRef} className="flex w-full flex-col gap-3">
      {accountSpotlight && (
        <TourSpotlight
          target={accountSpotlight}
          stepKey={highlightAccountId ?? 'account'}
          labelledBy=""
          aboveSheets
          showPopover={false}
        >
          {null}
        </TourSpotlight>
      )}
      {/* Invites addressed to the logged-in user */}
      <PendingInvites />
      {/* Add account + show disabled accounts toggle */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'h-7 shrink-0 cursor-pointer gap-1.5 px-3',
              addAccountSpotlit && ADD_ACCOUNT_SPOTLIGHT_CLASS
            )}
            onClick={handleAddAccountClick}
          >
            <PlusIcon className="size-3.5" />
            {t('addAccount')}
          </Button>
          <Tooltip tapToOpen>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={t('availableExchanges')}
                className="text-foreground/45 hover:text-foreground/70 flex size-6 cursor-help items-center justify-center rounded-full transition-colors"
              >
                <InfoIcon className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start">
              <p>
                {t('availableExchanges')}:{' '}
                {addableExchanges.map((exchange) => exchange.label).join(', ')}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="show-disabled" className="text-foreground/60 cursor-pointer text-xs">
            {t('showSuspendedAccounts')}
          </label>
          <Switch
            id="show-disabled"
            checked={showDisabled}
            onCheckedChange={(checked) => setShowDisabled(checked)}
          />
        </div>
      </div>
      {isAccountsLoading ? (
        <AccountListSkeleton />
      ) : (
        accounts?.map((account, i, origin) => {
          const isLastItem = origin.length - 1 === i
          const isEditing = editingAccountId === account.accountId
          return (
            <div
              className="flex w-full flex-col gap-3"
              key={account.accountId}
              data-settings-account-id={account.accountId}
            >
              <AccountRow
                item={account}
                isEditing={isEditing}
                isSavedExchange={savedExchangeAccountId === account.accountId}
                oauthConfig={oauthConfigByVenue.get(account.tradeVenue)}
                setApiFormState={setApiFormState}
                setSavedExchange={setSavedExchange}
                openManageAccess={manageAccessAccountId === account.accountId}
              />
              <AnimatePresence>
                {isEditing &&
                  account.authMethod !== AccountAuthMethod.ACCOUNT_AUTH_METHOD_APP_USER &&
                  !isAgentWalletAccount(account) && <ApiCredentialsForm />}
              </AnimatePresence>
              {!isLastItem && <Separator />}
            </div>
          )
        })
      )}
      {/* Empty state */}
      {!isAccountsLoading && (!accounts || accounts.length === 0) && (
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
          <div className="bg-foreground/5 flex size-12 items-center justify-center rounded-full">
            <span className="text-2xl">🔗</span>
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-foreground text-sm font-medium">{t('noAccountsConnected')}</p>
            <p className="text-foreground/60 text-xs">{t('connectExchangeAccount')}</p>
          </div>
          <Button
            variant="basic"
            size="sm"
            className={cn('w-fit gap-1.5', addAccountSpotlit && ADD_ACCOUNT_SPOTLIGHT_CLASS)}
            onClick={handleAddAccountClick}
          >
            <PlusIcon className="size-3.5" />
            {t('addAccount')}
          </Button>
        </div>
      )}
      <AccountConnectionSheet
        open={showAddAccount}
        onOpenChange={setShowAddAccount}
        onComplete={async () => {
          await queryClient.invalidateQueries({ queryKey: ['accounts'] })
        }}
      />
    </div>
  )
}

const AccountListSkeleton = () => (
  <div className="flex w-full flex-col gap-3">
    {Array.from({ length: 3 }).map((_, index) => (
      <div key={index} className="flex flex-col gap-3 py-3">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Skeleton className="bg-foreground/10 size-4 rounded-full" />
            <Skeleton className="bg-foreground/10 h-4 w-20" />
            <Skeleton className="bg-foreground/10 h-4 w-28" />
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="bg-foreground/10 h-3 w-24" />
            <Skeleton className="bg-foreground/10 h-4 w-20" />
          </div>
        </div>
        <div className="flex w-full items-center justify-between gap-2">
          <Skeleton className="bg-foreground/10 h-3 w-32" />
          <div className="flex items-center gap-2">
            <Skeleton className="bg-foreground/10 size-5" />
            <Skeleton className="bg-foreground/10 size-5" />
            <Skeleton className="bg-foreground/10 size-5" />
          </div>
        </div>
        {index < 2 && <Separator />}
      </div>
    ))}
  </div>
)

/** Shared yellow warning-box shell used by the reconnect banner and the plain
 * auth-error callout, so both stay visually in sync. */
// An inline notice inside the account list: a gold accent line and tint rather than a
// boxed panel (no nested bordered panels in Settings).
const AgentWalletWarningBanner = ({ children }: { children: React.ReactNode }) => (
  <div className="border-ox-gold/50 bg-ox-gold/5 flex w-full flex-col gap-2 border-l-2 px-3 py-2.5">
    {children}
  </div>
)

const AccountRow = memo(function AccountRow({
  item,
  isEditing,
  isSavedExchange,
  oauthConfig,
  setApiFormState,
  setSavedExchange,
  openManageAccess = false,
}: {
  item: PublicAccount
  isEditing: boolean
  isSavedExchange: boolean
  oauthConfig?: OauthConfig
  setApiFormState: AtomSetter<typeof apiCredentialsFormStateAtom>
  setSavedExchange: AtomSetter<typeof exchangeAtom>
  openManageAccess?: boolean
}) {
  const t = useTranslations('settings')
  // Lighter-specific copy (`lighterManageSubaccounts`) lives under
  // `accountConnection` — the same namespace the connect wizard itself reads
  // from (Task 8) — rather than duplicating it under `settings`.
  const tAccountConnection = useTranslations('accountConnection')
  const queryClient = useQueryClient()
  const [selectedAction, setSelectedAction] = useState<ButtonAction>(ButtonAction.DEFAULT)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showCancelSetupDialog, setShowCancelSetupDialog] = useState(false)
  const [showLeaveDialog, setShowLeaveDialog] = useState(false)
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false)
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const [showManage, setShowManage] = useState(openManageAccess)
  useEffect(() => {
    if (openManageAccess) setShowManage(true)
  }, [openManageAccess])
  // A Derive account that's still mid-setup (never reached ACTIVE, so no
  // subaccount is linked yet) needs the full connect wizard's picker step to
  // finish — prepare/confirm alone only re-establishes the wallet-level
  // session key. Route "Resume setup" through the wizard (pre-selected to
  // Derive) instead of reimplementing the connect flow inline; it already
  // finds this exact CREATING account via `findResumableAgentWalletAccount`
  // and picks the flow back up at the right step.
  const [showResumeDeriveSetup, setShowResumeDeriveSetup] = useState(false)
  // Lighter has no session key and no agent wallet (docs/lighter-frontend.md)
  // — there's nothing to silently refresh, ever. A still-CREATING account
  // (never linked) reopens the full connect wizard's picker, same as Derive's
  // resume-setup path. An already-linked account whose key went AUTH_FAILED
  // instead rotates that exact account's key via `showRelinkLighterKey`
  // below — reopening the wizard there can't relink the existing account (its
  // resumable-account matching only finds CREATING ones), it would mint a
  // duplicate sibling instead.
  const [showResumeLighterSetup, setShowResumeLighterSetup] = useState(false)
  const [showRelinkLighterKey, setShowRelinkLighterKey] = useState(false)
  const [showLighterFeeConsent, setShowLighterFeeConsent] = useState(false)
  // Papertrade re-sign: the wallet the user just connected, once set the sign dialog is open.
  // The wallet is only used for this account; closing the dialog drops it.
  const [papertradeResignWallet, setPapertradeResignWallet] = useState<string | null>(null)
  const accountStatus = toAccountStatus(item)
  const isOwner = isAccountOwner(item.role)
  // Absent (or zero-epoch) `firstActiveAt` means the account has never reached
  // ACTIVE — no trade history is possible, so it's safe to offer the
  // hard-delete-capable "Cancel setup" action instead of the regular Delete
  // (soft-disable) flow. '0' and undefined both mean "never activated" — see
  // docs/account-lifecycle-frontend.md §2.
  const isPendingSetup = !item.firstActiveAt || item.firstActiveAt === '0'
  const isAppUser = item.authMethod === AccountAuthMethod.ACCOUNT_AUTH_METHOD_APP_USER
  // Hyperliquid agent-wallet accounts have no API key/secret to edit — the agent
  // is minted server-side. Editing is Rename + Disable only; re-authorizing a
  // revoked/expired agent means re-running onboarding on the existing account.
  // Detected by venue too, so accounts still mid-onboarding (status CREATING,
  // authMethod not yet AGENT_WALLET) get the same treatment instead of the
  // nonsensical API-key edit form.
  const isAgentWallet = isAgentWalletAccount(item)
  const { connect } = useVenueOauthConnect()
  const { onboard: onboardHyperliquid } = useHyperliquidOnboard()
  const isDerive = item.tradeVenue === TradeVenue.TRADE_VENUE_DERIVE
  const isLighter = item.tradeVenue === TradeVenue.TRADE_VENUE_LIGHTER
  const isPapertrade = item.tradeVenue === TradeVenue.TRADE_VENUE_PAPERTRADE
  const papertradeDaysLeft =
    isPapertrade && item.status === AccountStatus.ACCOUNT_STATUS_ACTIVE
      ? daysUntil(Number(item.agentValidUntil ?? 0), Date.now())
      : null
  const canBindParadigm = supportsParadigmBinding(item.tradeVenue)
  const { registerSessionKey: registerDeriveSessionKey, listSubaccounts: listDeriveSubaccounts } =
    useDeriveOnboard()
  const [isReconnecting, setIsReconnecting] = useState(false)
  const reconnect = hyperliquidReconnectState(item)
  const bannerKey = reconnectBannerKey(item.tradeVenue, reconnect.reason)
  const [reconnectError, setReconnectError] = useState('')

  // Drop a stale error once the account's server-side state actually moves on
  // (e.g. crosses into a new reconnect reason), so an old failure never gets
  // misattributed to a later, unrelated banner state.
  useEffect(() => {
    setReconnectError('')
  }, [item.status, item.agentValidUntil])

  // Both the OAuth reconnect and the Hyperliquid onboarding calls reject with
  // either a real Error or a plain {message} shape (ErrorResponse / proto
  // Error) — never assume `instanceof Error`, or the real backend message
  // (e.g. "Must deposit before performing actions") gets discarded.
  const extractErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error) return error.message
    if (typeof (error as { message?: unknown })?.message === 'string') {
      return (error as { message: string }).message
    }
    return fallback
  }

  const handleReconnect = async () => {
    if (isReconnecting) return
    if (!oauthConfig) {
      toast.error(t('oauthNotConfigured'))
      return
    }
    setIsReconnecting(true)
    try {
      await connect({ accountId: item.accountId, venue: item.tradeVenue, oauthConfig })
      await queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast.success(t('reconnectionSubmitted'))
    } catch (error) {
      toast.error(extractErrorMessage(error, t('failedReconnectAccount')))
    } finally {
      setIsReconnecting(false)
    }
  }

  // Agent-wallet-venue analog of "Reconnect": re-run onboarding on the
  // existing account. Hyperliquid is prepare → wallet sign → confirm in one
  // step. Derive is NOT — confirm only re-establishes the wallet-level
  // session key; an account still mid-setup (never gone ACTIVE) also needs a
  // subaccount linked, which only the connect wizard's picker step does (see
  // `showResumeDeriveSetup` above), so that case is handled before this
  // function's own prepare/confirm-only path (correct for an ALREADY-linked
  // Derive account whose session key merely expired). Lighter never has a
  // headless prepare/confirm-only path at all either, but it splits the other
  // way from Derive: a still-CREATING account reopens the wizard's picker
  // (`showResumeLighterSetup`, same as Derive's resume-setup), while an
  // already-linked account whose key is broken rotates that one account's key
  // via `showRelinkLighterKey` instead of reopening the wizard — the wizard's
  // resumable-account matching can't find an already-ACTIVE account, so it
  // would mint a duplicate sibling rather than fix the existing one. Backend
  // errors (e.g. "Must deposit before performing actions" / wallet-already-
  // linked) arrive as Error.message and surface verbatim under the banner.
  const handleReauthorize = async () => {
    if (isReconnecting) return
    // One table decides the route per venue (see agent-wallet-route.ts); an
    // agent-wallet venue with no entry does nothing rather than hit Hyperliquid.
    const route = reauthorizeRoute(item.tradeVenue, isPendingSetup)
    if (route === null) return
    if (route === 'resume-derive') {
      setShowResumeDeriveSetup(true)
      return
    }
    if (route === 'resume-lighter') {
      setShowResumeLighterSetup(true)
      return
    }
    if (route === 'relink-lighter') {
      setShowRelinkLighterKey(true)
      return
    }
    setIsReconnecting(true)
    setReconnectError('')
    try {
      if (route === 'resign-papertrade') {
        // Connect inside the click, then hand the wallet to the sign dialog.
        // The public account doesn't expose the linked wallet, so a wrong
        // wallet is rejected by the backend and shown by the sign step.
        setPapertradeResignWallet(await connectWallet())
        return
      }
      if (route === 'resign-derive') {
        const walletAddress = await connectWallet()
        // A session key names the subaccounts it may trade, and replacing the
        // stored one with a narrower key would stop this user's other Derive
        // accounts on the same wallet. So list first — on the wallet's own
        // login header, which needs no key — and re-register for everything
        // they already have linked.
        const login = await signDeriveLogin(walletAddress)
        const list = await listDeriveSubaccounts(item.accountId, walletAddress, login)
        const scope = linkedSubaccountIds(list.subaccounts)
        if (scope.length === 0) {
          throw new Error(t('deriveReauthorizeNothingLinked'))
        }
        await registerDeriveSessionKey(item.accountId, walletAddress, scope)
      } else {
        // Only 'onboard-hyperliquid' remains: every other route returned above.
        await onboardHyperliquid(item.accountId)
      }
      await queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast.success(t('reconnectionSubmitted'))
    } catch (error) {
      const message = extractErrorMessage(error, t('failedReconnectAccount'))
      setReconnectError(message)
      toast.error(message)
    } finally {
      setIsReconnecting(false)
    }
  }

  const { mutateAsync: disableAccountMutation, isPending: isDeleting } = useDisableAccount({
    onSuccess: async () => {
      // Clear savedExchange if deleted account is the current selection
      if (isSavedExchange) {
        setSavedExchange(null)
      }
      await queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast.success(t('accountDeletedSuccess'))
      setShowDeleteDialog(false)
    },
    onError: () => {
      toast.error(t('failedDeleteAccount'))
    },
  })

  // The backend re-decides what "Cancel setup" actually does against a fresh
  // read (hard-delete, soft-disable instead, or reject) — always toast the
  // returned `message` verbatim rather than a hardcoded "deleted" string.
  // See docs/account-lifecycle-frontend.md §3.
  const { mutateAsync: cancelPendingAccountMutation, isPending: isCancellingSetup } =
    useCancelPendingAccount({
      onSuccess: (data) => {
        // useCancelPendingAccount already invalidates accountKeys.all(userId)
        // internally — no need to invalidate again here.
        if (isSavedExchange) {
          setSavedExchange(null)
        }
        toast.success(data.message)
        setShowCancelSetupDialog(false)
      },
      onError: async (error) => {
        // Several rejection reasons (already active, concurrent modification)
        // mean the account's real state has moved on since the last fetch —
        // refetch so the row's buttons reflect it. See docs/account-lifecycle-frontend.md §3.
        await queryClient.invalidateQueries({ queryKey: ['accounts'] })
        toast.error(error.message || t('failedCancelSetup'))
      },
    })

  const { mutateAsync: leaveAccountMutation, isPending: isLeaving } = useLeaveAccount({
    onSuccess: (data) => {
      if (isSavedExchange) {
        setSavedExchange(null)
      }
      toast.success(data.message)
      setShowLeaveDialog(false)
    },
    onError: (error) => {
      toast.error(error.message || t('failedLeaveAccount'))
    },
  })

  const onDisconnectSuccess = () => {
    // Clear savedExchange if the disconnected account is the current selection
    if (isSavedExchange) {
      setSavedExchange(null)
    }
    toast.success(t('accountDisconnectedSuccess'))
    setShowDisconnectDialog(false)
  }
  const onDisconnectError = (error: { message?: string }) => {
    toast.error(error.message || t('failedDisconnectAccount'))
  }
  const { mutateAsync: disconnectHyperliquidMutation, isPending: isDisconnectingHyperliquid } =
    useHyperliquidDisconnect({ onSuccess: onDisconnectSuccess, onError: onDisconnectError })
  const { mutateAsync: disconnectDeriveMutation, isPending: isDisconnectingDerive } =
    useDeriveDisconnect({ onSuccess: onDisconnectSuccess, onError: onDisconnectError })
  const { mutateAsync: disconnectPapertradeMutation, isPending: isDisconnectingPapertrade } =
    usePapertradeDisconnect({ onSuccess: onDisconnectSuccess, onError: onDisconnectError })
  const isDisconnecting =
    isDisconnectingHyperliquid || isDisconnectingDerive || isDisconnectingPapertrade
  // No Lighter disconnect RPC exists (docs/lighter-frontend.md): `disconnectRoute`
  // is null for it (and any venue without one), and the disconnect button's
  // onClick and the button-list filter below both gate those out before
  // `showDisconnectDialog` can ever fire.
  const disconnectAgentMutation = (accountId: string) => {
    switch (disconnectRoute(item.tradeVenue)) {
      case 'derive':
        return disconnectDeriveMutation(accountId)
      case 'papertrade':
        return disconnectPapertradeMutation(accountId)
      case 'hyperliquid':
        return disconnectHyperliquidMutation(accountId)
      default:
        return Promise.resolve()
    }
  }

  const resetForm = useCallback(() => {
    setApiFormState({
      apiKey: '',
      apiSecret: '',
      accountName: '',
      tradeVenue: TradeVenue.TRADE_VENUE_UNSPECIFIED,
      editingAccount: null,
    })
  }, [setApiFormState])

  const handleEditButton = () => {
    if (isEditing) {
      setSelectedAction(ButtonAction.DEFAULT)
      resetForm()
    } else {
      setApiFormState({
        apiKey: '',
        apiSecret: '',
        accountName: item.name,
        tradeVenue: item.tradeVenue,
        editingAccount: item,
      })
    }
  }

  const handleButtonClick = (key: string) => {
    if (key === 'manage') {
      setShowManage((prev) => !prev)
      // Close the credentials form if it is open for this account
      if (isEditing) {
        resetForm()
      }
      return
    }
    if (key === 'rename') {
      setShowRenameDialog(true)
      return
    }
    if (key === 'leave') {
      setShowLeaveDialog(true)
      return
    }

    if (key.toUpperCase() === selectedAction) {
      setSelectedAction(ButtonAction.DEFAULT)
      resetForm()
      return
    }

    switch (key) {
      case 'edit':
        if (isAgentWallet) {
          void handleReauthorize()
          break
        }
        if (isAppUser) {
          void handleReconnect()
          break
        }
        setShowManage(false)
        setSelectedAction(ButtonAction.EDIT)
        handleEditButton()
        break
      case 'delete':
        setShowDeleteDialog(true)
        break
      case 'cancelSetup':
        setShowCancelSetupDialog(true)
        break
      case 'disconnect':
        // No Lighter disconnect RPC exists — the button is disabled (see the
        // button-render loop below), but guard the handler too rather than
        // relying solely on the `disabled` attribute.
        if (disconnectRoute(item.tradeVenue) === null) break
        setShowDisconnectDialog(true)
        break
    }
  }

  const handleDeleteConfirm = async () => {
    await disableAccountMutation(item.accountId)
  }

  // Sync selectedAction with editingAccount state
  useEffect(() => {
    // Reset when form is cancelled (editingAccount becomes null)
    if (!isEditing) {
      setSelectedAction(ButtonAction.DEFAULT)
    }
  }, [isEditing])
  return (
    <div className="flex flex-col items-end gap-3 py-3">
      {/* Account Status */}
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex items-center gap-1.5">
          <ExchangeIcon
            exchange={tradeVenueToIconKey(item.tradeVenue) as any}
            className="size-4 shrink-0 rounded-full"
            logoObjectFit="contain"
          />
          <p className="text-foreground text-sm tracking-tight">
            {getExchangeByTradeVenue(item.tradeVenue)?.label ?? ''}
          </p>
          <p className="text-foreground/80 ml-0.5 text-sm tracking-tight">{item.name}</p>
          {!isOwner && (
            <span className="border-foreground/15 text-foreground/50 border px-2 py-0.5 text-[10px]">
              {t(`role.${accountRoleTranslationKey(item.role)}`)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <time className="text-foreground/40 font-sans text-xs tracking-tight">
            {t('checked', {
              time: formatDistanceToNowStrict(Number(item.updatedAt), { addSuffix: true }),
            })}
          </time>
          <div className="flex items-center gap-2">
            {papertradeDaysLeft !== null && (
              <span className="text-foreground/50 text-xs">
                {t('papertradeExpiresIn', { days: papertradeDaysLeft })}
              </span>
            )}
            <p className="text-foreground/80 text-sm">
              {t(`accountStatus.${accountStatus.statusType}`)}
            </p>
            {accountStatus.isActive ? (
              <CheckCircle2Icon className="text-chart-mint size-3.5" />
            ) : (
              <XCircleIcon className="text-chart-red size-3.5" />
            )}
          </div>
        </div>
      </div>
      {isAgentWallet && reconnect.needsReconnect && bannerKey !== null ? (
        <AgentWalletWarningBanner>
          {/* SettingsRow grammar: what's wrong on the left, the one fix on the right (wraps
              under it when the dialog is narrow). */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex min-w-0 flex-1 items-start gap-2">
              <AlertTriangleIcon className="text-ox-gold/80 mt-0.5 size-3.5 shrink-0" />
              <div className="flex min-w-0 flex-col gap-0.5">
                <p className="text-ox-gold/90 text-xs leading-relaxed">
                  {t(bannerKey, { days: reconnect.daysLeft ?? 0 })}
                </p>
                {/* The venue's raw error is supporting detail — muted, not a second warning. */}
                {item.lastAuthError && (
                  <p className="text-foreground/50 text-xs leading-relaxed break-words">
                    {item.lastAuthError}
                  </p>
                )}
                {reconnectError && (
                  <p className="text-chart-red text-xs leading-relaxed">{reconnectError}</p>
                )}
              </div>
            </div>
            {/* Same size/variant as every other Settings action. The label stays put while it
                works — the icon spins instead — so the button never changes width under the
                pointer. */}
            <Button
              variant="secondary"
              size="sm"
              type="button"
              className="shrink-0"
              disabled={isReconnecting}
              aria-busy={isReconnecting}
              aria-label={isReconnecting ? t('refreshingConnection') : undefined}
              onClick={() => void handleReauthorize()}
            >
              <RefreshCwIcon
                className={cn('size-3.5', isReconnecting && 'animate-spin')}
                aria-hidden="true"
              />
              {isPapertrade ? t('resignConnection') : t('refreshConnection')}
            </Button>
          </div>
        </AgentWalletWarningBanner>
      ) : isAgentWallet && reconnectError ? (
        // Reauthorize was triggered from the edit-icon on an account that isn't
        // currently flagged as needing reconnect (e.g. still healthy) — the
        // verbatim error still needs a persistent home, not just a toast.
        <AgentWalletWarningBanner>
          <div className="flex items-start gap-2">
            <AlertTriangleIcon className="text-ox-gold/70 mt-0.5 size-3.5 shrink-0" />
            <p className="text-chart-red text-xs leading-relaxed">{reconnectError}</p>
          </div>
        </AgentWalletWarningBanner>
      ) : (
        item.lastAuthError && (
          <AgentWalletWarningBanner>
            <div className="flex items-start gap-2">
              <AlertTriangleIcon className="text-ox-gold/70 mt-0.5 size-3.5 shrink-0" />
              <p className="text-ox-gold/80 text-xs leading-relaxed">{item.lastAuthError}</p>
            </div>
          </AgentWalletWarningBanner>
        )
      )}
      {/* Paradigm desk binding. Deribit and Bybit only, and the one place in the app a desk
          can be connected or re-pointed at a different nickname — see ParadigmBindingRow. */}
      {canBindParadigm && <ParadigmBindingRow account={item} />}
      <div className="flex w-full items-center justify-between gap-2">
        <pre className="text-foreground/60 text-xs">IP: {item.publicIp}</pre>
        {/* Action buttons. Credential edit and delete are owner-only; the member
            list is visible to every member (read-only for non-owners). */}
        <div className="flex items-center gap-2">
          {ACTION_BUTTONS_CONSTANT.filter((button) => {
            if (button.key === 'disconnect') {
              // No agent has ever been minted for a still-onboarding account —
              // nothing to disconnect yet.
              if (!isAgentWallet || item.status === AccountStatus.ACCOUNT_STATUS_CREATING) {
                return false
              }
              // Lighter has no separate agent/session key to revoke — the
              // credential IS an L2 keypair registered on-chain, and undoing
              // that needs an L1 wallet signature we can't produce server-side.
              // "Delete" is the removal path (it clears the credential and
              // releases the subaccount reservation), so a second button here
              // would only ever be a dead control.
              if (disconnectRoute(item.tradeVenue) === null) return false
            }
            // "Cancel setup" (hard-delete-capable) replaces "Delete" (soft-disable)
            // for accounts that have never reached ACTIVE — see isPendingSetup.
            const hiddenForPendingSetup = isPendingSetup ? 'delete' : 'cancelSetup'
            if (button.key === hiddenForPendingSetup) return false
            return isOwner ? !button.nonOwnerOnly : !button.ownerOnly
          }).map((button) => {
            const isEditAsReconnect = (isAppUser || isAgentWallet) && button.key === 'edit'
            // Lighter's edit/pencil action is always "Manage subaccounts" —
            // unlike Derive/Hyperliquid the label doesn't distinguish resuming
            // a still-CREATING account from rotating an already-linked one's
            // key (see `handleReauthorize`'s isLighter branch: the two cases
            // open different sheets — wizard picker vs. relink — but share
            // this one label).
            const buttonLabel = isEditAsReconnect
              ? isReconnecting
                ? t('refreshingConnection')
                : isLighter
                  ? tAccountConnection('lighterManageSubaccounts')
                  : isAgentWallet
                    ? accountStatus.statusType === 'creating'
                      ? t('resumeAgentWalletSetup')
                      : t('reauthorizeAgentWallet')
                    : t('reconnectViaExchangeLogin')
              : button.key === 'manage' && !isOwner
                ? t('viewMembers')
                : t(button.labelKey)
            const buttonIcon = isEditAsReconnect ? (
              isReconnecting ? (
                <Loader2Icon className="text-foreground size-3 animate-spin" />
              ) : (
                <RefreshCwIcon className="text-foreground size-3" />
              )
            ) : (
              button.icon
            )
            const isActive =
              (selectedAction === ButtonAction.EDIT &&
                button.type === ButtonAction.EDIT &&
                !isAppUser &&
                !isAgentWallet) ||
              (showManage && button.type === ButtonAction.MANAGE)
            return (
              <Tooltip key={button.key}>
                <TooltipTrigger asChild>
                  <button
                    className={cn([
                      'border-foreground/20 text-foreground/65 hover:bg-foreground/5 hover:text-foreground flex size-5 items-center justify-center border transition-colors',
                      isActive && 'bg-emphasis/80 text-emphasis-foreground',
                      isEditAsReconnect && isReconnecting && 'cursor-wait opacity-70',
                    ])}
                    type="button"
                    disabled={isEditAsReconnect && isReconnecting}
                    onClick={() => handleButtonClick(button.key)}
                  >
                    {cloneElement(buttonIcon, {
                      className: cn([
                        'size-3 text-current',
                        isEditAsReconnect && isReconnecting && 'animate-spin',
                      ]),
                    })}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="whitespace-pre-line">{buttonLabel}</p>
                </TooltipContent>
              </Tooltip>
            )
          })}

          {/* Lighter Partner Attribution consent. Appended rather than added to
              ACTION_BUTTONS_CONSTANT because it is the only venue-specific
              action here — every other entry in that list applies to all
              venues. Only meaningful once the account is linked and trading
              (an account still CREATING has no registered L2 key to sign the
              tx's L2 half), and admin-gated server-side, so non-owners don't
              see a control that would be refused. */}
          {isLighter && isOwner && item.status === AccountStatus.ACCOUNT_STATUS_ACTIVE && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="border-foreground/20 text-foreground/65 hover:bg-foreground/5 hover:text-foreground flex size-5 items-center justify-center border transition-colors"
                  type="button"
                  onClick={() => {
                    // The sheet goes straight to the wallet signature, so the
                    // signing window has to be opened here — synchronously in
                    // the click — since transient activation is gone by the
                    // time the sheet's prepare call resolves. A throw only
                    // means the sheet surfaces it and offers Try again.
                    try {
                      beginManualWalletAction()
                    } catch {
                      // handled by the sheet
                    }
                    setShowLighterFeeConsent(true)
                  }}
                >
                  <PercentIcon className="size-3 text-current" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="whitespace-pre-line">
                  {tAccountConnection('lighterFeeConsentAction')}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Manage access (members, pending invites, invite form) */}
      <AnimatePresence>{showManage && <ManageAccessPanel account={item} />}</AnimatePresence>

      {/* Delete Confirmation Dialog */}
      <DestructiveConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title={t('deleteAccount')}
        description={t('deleteAccountDescription', { name: item.name })}
        cancelLabel={t('cancel')}
        confirmLabel={t('delete')}
        pendingLabel={t('deleting')}
        isPending={isDeleting}
        onConfirm={handleDeleteConfirm}
      />

      {/* Cancel Setup Confirmation Dialog (never-activated accounts) */}
      <DestructiveConfirmDialog
        open={showCancelSetupDialog}
        onOpenChange={setShowCancelSetupDialog}
        title={t('cancelSetup')}
        description={t('cancelSetupDescription', { name: item.name })}
        cancelLabel={t('cancel')}
        confirmLabel={t('confirmCancelSetup')}
        pendingLabel={t('cancellingSetup')}
        isPending={isCancellingSetup}
        onConfirm={() => cancelPendingAccountMutation(item.accountId)}
      />

      {/* Leave Confirmation Dialog (shared accounts) */}
      <DestructiveConfirmDialog
        open={showLeaveDialog}
        onOpenChange={setShowLeaveDialog}
        title={t('leaveAccount')}
        description={t('leaveAccountDescription', { name: item.name })}
        cancelLabel={t('cancel')}
        confirmLabel={t('leave')}
        pendingLabel={t('leaving')}
        isPending={isLeaving}
        onConfirm={() => leaveAccountMutation(item.accountId)}
      />

      {/* Disconnect Confirmation Dialog (agent-wallet / session-key venues) */}
      <DestructiveConfirmDialog
        open={showDisconnectDialog}
        onOpenChange={setShowDisconnectDialog}
        title={t('disconnectAgentWallet')}
        description={
          isDerive
            ? t('deriveDisconnectWarning')
            : isPapertrade
              ? t('papertradeDisconnectWarning')
              : t('disconnectAgentWalletDescription', { name: item.name })
        }
        cancelLabel={t('cancel')}
        confirmLabel={t('disconnect')}
        pendingLabel={t('disconnecting')}
        isPending={isDisconnecting}
        onConfirm={() => disconnectAgentMutation(item.accountId)}
      />

      {/* Papertrade re-sign: wallet already connected by handleReauthorize. */}
      <Dialog
        open={papertradeResignWallet !== null}
        onOpenChange={(open) => {
          if (!open) setPapertradeResignWallet(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('reauthorizeAgentWallet')}</DialogTitle>
          </DialogHeader>
          {papertradeResignWallet !== null && (
            <PapertradeSignStep
              accountId={item.accountId}
              wallet={papertradeResignWallet}
              onDone={() => {
                setPapertradeResignWallet(null)
                toast.success(t('reconnectionSubmitted'))
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Rename Dialog (any member can relabel the account for themselves) */}
      <RenameAccountDialog
        account={item}
        open={showRenameDialog}
        onOpenChange={setShowRenameDialog}
      />

      {/* Resume a Derive account stuck mid-setup: the full connect wizard,
          pre-selected to Derive so it finds and continues this exact
          CREATING account rather than starting a fresh one. */}
      <AccountConnectionSheet
        open={showResumeDeriveSetup}
        onOpenChange={setShowResumeDeriveSetup}
        initialVenue={TradeVenue.TRADE_VENUE_DERIVE}
        onComplete={async () => {
          await queryClient.invalidateQueries({ queryKey: ['accounts'] })
          toast.success(t('reconnectionSubmitted'))
        }}
      />

      {/* Resume a still-CREATING Lighter account: the full connect wizard,
          pre-selected to Lighter, so its own resumable-account matching picks
          this exact CREATING account back up. An already-linked account whose
          key needs replacing does NOT use this — the wizard's resumable
          matching only finds CREATING accounts, so reopening it here would
          mint a duplicate sibling instead of fixing this one; that case is
          `LighterKeyRelinkSheet` below. */}
      <AccountConnectionSheet
        open={showResumeLighterSetup}
        onOpenChange={setShowResumeLighterSetup}
        initialVenue={TradeVenue.TRADE_VENUE_LIGHTER}
        onComplete={async () => {
          await queryClient.invalidateQueries({ queryKey: ['accounts'] })
          toast.success(t('reconnectionSubmitted'))
        }}
      />

      {/* Rotate the key for an already-linked Lighter account whose key went
          AUTH_FAILED — reuses the same lighterLinkSubaccounts RPC a fresh
          link uses, resubmitting the SAME slot this account already holds.
          `item` (PublicAccount) never exposes lighterAccountIndex/
          lighterApiKeyIndex — those live only on the internal Account
          message — so the sheet re-derives its own slot via
          lighterListSubaccounts once a wallet connects (see
          lighter-key-relink-sheet.tsx); no need to gate this mount on
          anything besides accountId, which every account has. */}
      <LighterKeyRelinkSheet
        open={showRelinkLighterKey}
        onOpenChange={setShowRelinkLighterKey}
        accountId={item.accountId}
        onComplete={async () => {
          await queryClient.invalidateQueries({ queryKey: ['accounts'] })
          toast.success(t('reconnectionSubmitted'))
        }}
      />

      {/* Grant Lighter Partner Attribution consent for this account. The
          trade-venue reads the resulting flag when it starts an account
          instance, so attribution begins on that account's next instance
          start rather than on the very next order. */}
      <LighterFeeConsentSheet
        open={showLighterFeeConsent}
        onOpenChange={setShowLighterFeeConsent}
        accountId={item.accountId}
        onComplete={async () => {
          await queryClient.invalidateQueries({ queryKey: ['accounts'] })
          toast.success(tAccountConnection('lighterFeeConsentDone'))
        }}
      />
    </div>
  )
})

const ACTION_BUTTONS_CONSTANT: {
  key: string
  type: ButtonAction
  labelKey:
    | 'updateApiKeyAndSecret'
    | 'rename'
    | 'manageAccess'
    | 'delete'
    | 'cancelSetup'
    | 'leaveAccount'
    | 'disconnectAgentWallet'
  icon: React.ReactElement<{ className?: string }>
  ownerOnly?: boolean
  nonOwnerOnly?: boolean
}[] = [
  {
    key: 'edit',
    type: ButtonAction.EDIT,
    labelKey: 'updateApiKeyAndSecret',
    icon: <PencilIcon className="text-foreground size-3" />,
    ownerOnly: true,
  },
  {
    // Anyone can rename — it updates the member's own label for the account, not
    // a global name, so it's exposed regardless of role, venue, or auth method.
    // (Agent-wallet/OAuth accounts have no credential-edit form, so this is the
    // only rename path for their owners.) Distinct icon avoids colliding with the
    // owner credential-edit pencil shown on API-key accounts.
    key: 'rename',
    type: ButtonAction.RENAME,
    labelKey: 'rename',
    icon: <TagIcon className="text-foreground size-3" />,
  },
  {
    // Visible to every member: owners get the full management panel, non-owners
    // a read-only roster (labelled "View members"). See ManageAccessPanel.
    key: 'manage',
    type: ButtonAction.MANAGE,
    labelKey: 'manageAccess',
    icon: <UsersIcon className="text-foreground size-3" />,
  },
  {
    key: 'delete',
    type: ButtonAction.DELETE,
    labelKey: 'delete',
    icon: <Trash2Icon className="text-foreground size-3" />,
    ownerOnly: true,
  },
  {
    // Never-activated accounts only (see isPendingSetup) — replaces "delete"
    // for the duration the account has no first_active_at.
    key: 'cancelSetup',
    type: ButtonAction.CANCEL_SETUP,
    labelKey: 'cancelSetup',
    icon: <XIcon className="text-foreground size-3" />,
    ownerOnly: true,
  },
  {
    key: 'leave',
    type: ButtonAction.LEAVE,
    labelKey: 'leaveAccount',
    icon: <LogOutIcon className="text-foreground size-3" />,
    nonOwnerOnly: true,
  },
  {
    // Agent-wallet / session-key venues (Hyperliquid, Derive): unlink the
    // agent wallet or session key (clears creds + releases the master-wallet
    // reservation). Owner-only; rendered only for agent-wallet accounts (see
    // the filter in the button map).
    key: 'disconnect',
    type: ButtonAction.DISCONNECT,
    labelKey: 'disconnectAgentWallet',
    icon: <Unplug className="text-foreground size-3" />,
    ownerOnly: true,
  },
]
